import { WIX_ECOM, WIX_NEW_STORES } from '@wix/app-definition-ids';

import { APP_WIDGET_LOGIN_MENU_ID } from '../../../../constants';
import { Experiment } from '../../../../constants/experiment';
import type { ContextProps } from '../../../../types/controller';
import { PLACEHOLDER_ORDER_ITEMS } from './editor-placeholder';
import {
  addToCart,
  buildEditorBuyAgainItems,
  fetchInStockOrderItems,
  type OrderItem,
} from './service';

const MAX_BUY_AGAIN_ITEMS = 3;
const STORES_PRODUCT_PAGE_SECTION_ID = 'product_page';

interface BuyAgainAddToCartEventPayload {
  type: 'onBuyAgainAddToCart';
  catalogItemId: string;
  catalogOptions?: Record<string, any>;
}

interface BuyAgainNavigateToProductEventPayload {
  type: 'onBuyAgainNavigateToProduct';
  catalogItemId: string;
}

enum CartClickAction {
  SideCart = 'sideCart',
  CartPage = 'cartPage',
}

interface LoginBarApi {
  readonly buyAgainSettings?: { cartClickAction?: CartClickAction };
  buyAgainOrderItems?: OrderItem[];
  onBuyAgainAddToCart?: (
    handler: (event: BuyAgainAddToCartEventPayload) => void,
  ) => void;
  onBuyAgainNavigateToProduct?: (
    handler: (event: BuyAgainNavigateToProductEventPayload) => void,
  ) => void;
  confirmBuyAgainItemAdded?: (catalogItemId: string, error?: unknown) => void;
}

interface EcomCartApi {
  reloadCart?: () => Promise<void>;
  openSideCart?: () => void;
}

interface EcomNavigateApi {
  toCart?: () => Promise<void>;
}

interface EcomPublicApi {
  cart?: EcomCartApi;
  navigate?: EcomNavigateApi;
}

type ResolvedEcomApi = EcomPublicApi &
  Required<Pick<EcomPublicApi, 'cart' | 'navigate'>>;

interface StoresPublicApi {
  navigate?: {
    toProductPage?: (args: { id: string }) => Promise<void>;
  };
}

export class BuyAgain {
  private readonly loginBars = new Set<LoginBarApi>();
  private readonly boundLoginBars = new WeakSet<LoginBarApi>();
  private knownCatalogIds = new Set<string>();
  private onLoginRegistered = false;

  private itemsCache: Promise<OrderItem[]> | null = null;
  private ecomApiCache: Promise<ResolvedEcomApi | null> | null = null;

  async init(contextProps: ContextProps): Promise<void> {
    const { wixCodeApi, flowAPI } = contextProps;
    const { environment, experiments, httpClient } = flowAPI;

    if (!experiments.enabled(Experiment.LoginSocialBarBuyAgain)) {
      return;
    }
    if (!(await this.isStoresInstalled(wixCodeApi))) {
      return;
    }

    const ecomApi = await this.getCachedEcomApi(wixCodeApi);
    if (!ecomApi) {
      this.reportError(
        contextProps,
        new Error(
          'Buy Again: Stores is installed but the ecom public API is unavailable',
        ),
      );
      return;
    }

    const [loginBar] = contextProps.$w(
      APP_WIDGET_LOGIN_MENU_ID,
    ) as LoginBarApi[];
    if (!loginBar) {
      return;
    }
    this.loginBars.add(loginBar);

    if (!environment.isViewer) {
      await this.bindItemsAndHandlers(
        contextProps,
        () =>
          buildEditorBuyAgainItems(
            httpClient,
            PLACEHOLDER_ORDER_ITEMS,
            MAX_BUY_AGAIN_ITEMS,
          ),
        PLACEHOLDER_ORDER_ITEMS.slice(0, MAX_BUY_AGAIN_ITEMS),
      );
      return;
    }

    const fetchOrders = () =>
      fetchInStockOrderItems(httpClient, MAX_BUY_AGAIN_ITEMS);

    if (!this.onLoginRegistered) {
      this.onLoginRegistered = true;
      wixCodeApi.user.onLogin(() => {
        this.itemsCache = null;
        return this.bindItemsAndHandlers(contextProps, fetchOrders);
      });
    }

    if (wixCodeApi.user.currentUser.loggedIn) {
      await this.bindItemsAndHandlers(contextProps, fetchOrders);
    }
  }

  private async bindItemsAndHandlers(
    contextProps: ContextProps,
    fetchItems: () => Promise<OrderItem[]>,
    fallback: OrderItem[] = [],
  ): Promise<void> {
    let items: OrderItem[];
    try {
      items = await this.fetchCachedItems(fetchItems);
    } catch (error) {
      this.reportError(contextProps, error);
      items = fallback;
    }

    this.knownCatalogIds = new Set(
      items.map((item) => item.catalogItemId).filter(Boolean),
    );

    await Promise.all(
      [...this.loginBars].map(async (loginBar) => {
        loginBar.buyAgainOrderItems = items;
        if (items.length > 0) {
          await this.bindHandlers(loginBar, contextProps);
        }
      }),
    );
  }

  private fetchCachedItems(
    fetchItems: () => Promise<OrderItem[]>,
  ): Promise<OrderItem[]> {
    if (!this.itemsCache) {
      this.itemsCache = fetchItems();
    }
    return this.itemsCache;
  }

  private async getCachedEcomApi(
    wixCodeApi: ContextProps['wixCodeApi'],
  ): Promise<ResolvedEcomApi | null> {
    if (!this.ecomApiCache) {
      this.ecomApiCache = wixCodeApi.site
        .getPublicAPI(WIX_ECOM)
        .then((api) => {
          const ecomApi = api as EcomPublicApi | null;
          return ecomApi?.cart && ecomApi.navigate
            ? (ecomApi as ResolvedEcomApi)
            : null;
        })
        .catch(() => null);
    }
    const ecomApi = await this.ecomApiCache;
    if (!ecomApi) {
      this.ecomApiCache = null;
    }
    return ecomApi;
  }

  private async bindHandlers(
    loginBar: LoginBarApi,
    contextProps: ContextProps,
  ): Promise<void> {
    if (this.boundLoginBars.has(loginBar)) {
      return;
    }
    this.boundLoginBars.add(loginBar);

    const ecomApi = await this.getCachedEcomApi(contextProps.wixCodeApi);
    if (!ecomApi) {
      return;
    }

    const { wixCodeApi, flowAPI } = contextProps;
    const { cart, navigate } = ecomApi;

    loginBar.onBuyAgainNavigateToProduct?.(async ({ catalogItemId }) => {
      if (!catalogItemId) {
        return;
      }
      try {
        const storesApi = (await wixCodeApi.site.getPublicAPI(
          WIX_NEW_STORES,
        )) as StoresPublicApi | null;
        await storesApi?.navigate?.toProductPage?.({ id: catalogItemId });
      } catch (error) {
        this.reportError(contextProps, error);
      }
    });

    loginBar.onBuyAgainAddToCart?.(
      async ({ catalogItemId, catalogOptions }) => {
        if (!this.knownCatalogIds.has(catalogItemId)) {
          loginBar.confirmBuyAgainItemAdded?.(catalogItemId);
          return;
        }
        try {
          await addToCart(flowAPI.httpClient, catalogItemId, catalogOptions);
          await cart.reloadCart?.();
          if (
            loginBar.buyAgainSettings?.cartClickAction ===
            CartClickAction.SideCart
          ) {
            cart.openSideCart?.();
          } else {
            await navigate.toCart?.();
          }
          loginBar.confirmBuyAgainItemAdded?.(catalogItemId);
        } catch (error) {
          this.reportError(contextProps, error);
          loginBar.confirmBuyAgainItemAdded?.(catalogItemId, error);
        }
      },
    );
  }

  private isStoresInstalled(
    wixCodeApi: ContextProps['wixCodeApi'],
  ): Promise<boolean> {
    return wixCodeApi.site.isAppSectionInstalled({
      sectionId: STORES_PRODUCT_PAGE_SECTION_ID,
      appDefinitionId: WIX_ECOM,
    });
  }

  private reportError(contextProps: ContextProps, error: unknown): void {
    const { errorHandler, panoramaClient } = contextProps.flowAPI;
    const { httpError } = errorHandler.getResolvedError(error);
    panoramaClient?.errorMonitor().reportError((httpError || error) as Error);
  }
}

const buyAgain = new BuyAgain();

export const initBuyAgain = (contextProps: ContextProps): Promise<void> =>
  buyAgain.init(contextProps);
